import { useEffect, useMemo, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { trpc } from '../lib/trpc.js';
import { formatGbp } from '../lib/format.js';
import { format, parse, addDays } from 'date-fns';
import { jsPDF } from 'jspdf';

type PayslipSearch = { tech: string; weekStart: string; autoprint?: '1' };

export function PayslipPage() {
  const search = useSearch({ strict: false }) as PayslipSearch;
  const { tech, weekStart, autoprint } = search;
  const week = trpc.timesheets.week.useQuery({ weekStart }, { enabled: Boolean(weekStart) });
  const sendPayout = trpc.payouts.send.useMutation();
  const [emailStatus, setEmailStatus] = useState<string>('');

  useEffect(() => {
    if (autoprint === '1' && week.data) {
      // Slight delay so layout settles before the print dialog
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [autoprint, week.data]);

  const days = useMemo(() => {
    if (!weekStart) return [];
    const start = parse(weekStart, 'yyyy-MM-dd', new Date());
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(start, i);
      return { dayKey: format(d, 'yyyy-MM-dd'), dayLabel: format(d, 'EEE'), dateLabel: format(d, 'd MMM yyyy') };
    });
  }, [weekStart]);

  function buildPdf(): { base64: string; doc: jsPDF } {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 50;
    let y = margin;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text('THE LONDON CARPET CLINIC', margin, y);
    y += 16;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text('cockpit.thecarpetclinic.co.uk', margin, y);

    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('WEEKLY PAYSLIP', 540, margin, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`${weekStartLabel} → ${weekEndLabel}`, 540, margin + 14, { align: 'right' });
    doc.setTextColor(0);

    y += 32;
    doc.setLineWidth(1);
    doc.line(margin, y, 545, y);

    y += 22;
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text('PAY TO', margin, y);
    doc.text('HOURLY RATE', 545, y, { align: 'right' });
    y += 14;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text(tech, margin, y);
    doc.text(rate > 0 ? `£${rate.toFixed(2)}/hr` : '— (rate not set)', 545, y, { align: 'right' });
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text('Status: Self-employed contractor', margin, y);
    doc.text(`Issued ${issueDate}`, 545, y, { align: 'right' });

    // Table
    y += 28;
    doc.setFillColor(243, 244, 246);
    doc.rect(margin, y - 12, 545 - margin, 18, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text('DAY', margin + 4, y);
    doc.text('HOURS', 360, y, { align: 'right' });
    doc.text('RATE', 440, y, { align: 'right' });
    doc.text('AMOUNT', 540, y, { align: 'right' });

    y += 12;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0);
    for (const r of rows) {
      doc.text(`${r.dayLabel}  ${r.dateLabel}`, margin + 4, y);
      doc.text(r.hours > 0 ? r.hours.toFixed(2) : '—', 360, y, { align: 'right' });
      doc.text(rate > 0 ? `£${rate.toFixed(2)}` : '—', 440, y, { align: 'right' });
      doc.text(r.pay > 0 ? `£${r.pay.toFixed(2)}` : '—', 540, y, { align: 'right' });
      doc.setDrawColor(229, 231, 235);
      doc.line(margin, y + 4, 545, y + 4);
      y += 18;
    }

    // Total row
    y += 4;
    doc.setLineWidth(2);
    doc.setDrawColor(15, 41, 66);
    doc.line(margin, y, 545, y);
    y += 16;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Total', margin + 4, y);
    doc.text(totalHours.toFixed(2), 360, y, { align: 'right' });
    if (rate > 0) doc.text(`× £${rate.toFixed(2)}`, 440, y, { align: 'right' });
    doc.text(`£${totalPay.toFixed(2)}`, 540, y, { align: 'right' });

    // Footer
    y += 36;
    doc.setLineWidth(0.5);
    doc.setLineDashPattern([2, 2], 0);
    doc.line(margin, y, 545, y);
    doc.setLineDashPattern([], 0);
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    const disclaimer = doc.splitTextToSize(
      'Self-employed contractor — gross payment, no PAYE tax or NI deducted at source. Contractor is responsible for declaring this income via Self Assessment.',
      495,
    );
    doc.text(disclaimer, margin, y);
    y += disclaimer.length * 11 + 6;
    doc.text('Issued by Gustavo Jiménez on behalf of The London Carpet Clinic.', margin, y);

    const base64 = doc.output('datauristring').split(',')[1] ?? '';
    return { base64, doc };
  }

  async function emailPayslip() {
    if (!tech || !weekStart) return;
    setEmailStatus('Building PDF…');
    const { base64 } = buildPdf();
    setEmailStatus('Sending…');
    try {
      const r = await sendPayout.mutateAsync({
        technicianName: tech,
        weekStart,
        pdfBase64: base64,
      });
      if (r.status === 'sent') setEmailStatus(`✓ Sent to ${r.sentToEmail}`);
      else if (r.status === 'manual') setEmailStatus(`⚠ ${r.errorMessage} — payout recorded`);
      else setEmailStatus(`✗ Failed: ${r.errorMessage}`);
    } catch (e) {
      setEmailStatus(`✗ Error: ${(e as Error).message}`);
    }
  }

  if (!tech || !weekStart) return <p className="text-red-600">Missing tech or weekStart param</p>;
  if (week.isLoading) return <p className="text-gray-500">Loading…</p>;
  if (!week.data) return <p className="text-gray-500">No data</p>;

  const techInfo = week.data.techs.find((t) => t.name === tech);
  if (!techInfo) {
    return <p className="text-red-600">Technician not found in this week.</p>;
  }

  const cellsByKey = new Map(week.data.cells.map((c) => [`${c.technicianName}|${c.dayKey}`, c]));
  const rate = techInfo.hourlyRateGbp ?? 0;
  const rows = days.map((d) => {
    const cell = cellsByKey.get(`${tech}|${d.dayKey}`);
    return {
      ...d,
      hours: cell?.hours ?? 0,
      pay: (cell?.hours ?? 0) * rate,
    };
  });
  const totalHours = rows.reduce((s, r) => s + r.hours, 0);
  const totalPay = rows.reduce((s, r) => s + r.pay, 0);

  const weekStartLabel = format(parse(weekStart, 'yyyy-MM-dd', new Date()), 'd MMM yyyy');
  const weekEndLabel = format(addDays(parse(weekStart, 'yyyy-MM-dd', new Date()), 6), 'd MMM yyyy');
  const issueDate = format(new Date(), 'd MMM yyyy');

  return (
    <div className="payslip-page">
      <style>{`
        @media print {
          @page { size: A4; margin: 18mm 16mm; }
          .payslip-noprint, header, nav, .payslip-noprint * { display: none !important; }
          body { background: white !important; }
          .payslip-page { margin: 0 !important; padding: 0 !important; max-width: none !important; }
        }
        .payslip-page { max-width: 700px; margin: 0 auto; padding: 24px; color: #0f2942; font-family: ui-sans-serif, system-ui; }
        .payslip-page h1 { margin: 0; font-size: 26px; letter-spacing: 0.5px; }
        .payslip-page table { width: 100%; border-collapse: collapse; margin-top: 14px; }
        .payslip-page th, .payslip-page td { border-bottom: 1px solid #e5e7eb; padding: 8px 6px; text-align: left; font-size: 13px; }
        .payslip-page th { background: #f3f4f6; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; }
        .payslip-page td.num, .payslip-page th.num { text-align: right; font-variant-numeric: tabular-nums; font-family: ui-monospace, monospace; }
        .payslip-page tr.total td { border-top: 2px solid #0f2942; border-bottom: none; padding-top: 10px; font-weight: 600; font-size: 14px; }
        .payslip-meta { display: flex; justify-content: space-between; align-items: flex-start; margin-top: 18px; }
        .payslip-foot { margin-top: 28px; padding-top: 14px; border-top: 1px dashed #d1d5db; font-size: 11px; color: #6b7280; line-height: 1.55; }
        .payslip-actions { display: flex; gap: 8px; margin-bottom: 16px; }
        .payslip-actions button { padding: 6px 14px; border-radius: 6px; border: 1px solid #0f2942; background: #0f2942; color: white; font-size: 13px; cursor: pointer; }
        .payslip-actions button.secondary { background: white; color: #0f2942; }
      `}</style>

      <div className="payslip-actions payslip-noprint">
        <button onClick={() => window.print()}>Print / Save as PDF</button>
        <button onClick={() => emailPayslip()} disabled={sendPayout.isPending}>
          {sendPayout.isPending ? 'Sending…' : 'Email to technician'}
        </button>
        <button className="secondary" onClick={() => window.close()}>Close</button>
        {emailStatus && <span style={{ alignSelf: 'center', fontSize: 13 }}>{emailStatus}</span>}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0f2942', paddingBottom: 14 }}>
        <div>
          <h1>The London Carpet Clinic</h1>
          <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>cockpit.thecarpetclinic.co.uk</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>WEEKLY PAYSLIP</p>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
            {weekStartLabel} → {weekEndLabel}
          </p>
        </div>
      </div>

      <div className="payslip-meta">
        <div>
          <p style={{ margin: 0, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Pay to</p>
          <p style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 600 }}>{tech}</p>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>Status: Self-employed contractor</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: 0, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Hourly rate</p>
          <p style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 600 }}>{rate > 0 ? `${formatGbp(rate)}/hr` : '— (rate not set)'}</p>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>Issued {issueDate}</p>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Day</th>
            <th>Date</th>
            <th className="num">Hours</th>
            <th className="num">Rate</th>
            <th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.dayKey}>
              <td>{r.dayLabel}</td>
              <td>{r.dateLabel}</td>
              <td className="num">{r.hours > 0 ? r.hours.toFixed(2) : '—'}</td>
              <td className="num">{rate > 0 ? formatGbp(rate) : '—'}</td>
              <td className="num">{r.pay > 0 ? formatGbp(r.pay) : '—'}</td>
            </tr>
          ))}
          <tr className="total">
            <td></td>
            <td>Total</td>
            <td className="num">{totalHours.toFixed(2)}</td>
            <td className="num">{rate > 0 ? `× ${formatGbp(rate)}` : ''}</td>
            <td className="num">{formatGbp(totalPay)}</td>
          </tr>
        </tbody>
      </table>

      <div className="payslip-foot">
        <p style={{ margin: 0 }}>
          Self-employed contractor — gross payment, no PAYE tax or NI deducted at source. Contractor is responsible for declaring this income and paying any tax/NI owed via Self Assessment.
        </p>
        <p style={{ margin: '6px 0 0' }}>
          Payment for week ending {weekEndLabel}, issued by Gustavo Jiménez on behalf of The Smart Clean / London Carpet Clinic.
        </p>
      </div>
    </div>
  );
}
